import { css, Paragraph, styled } from 'canoe';
import React, { useEffect, useRef, useState } from 'react';
import scrollIntoView from 'scroll-into-view-if-needed';

import { EntitlementName, useEntitlementAccess } from '@candu-ai/organizations/entitlements';
import { Flags, useFlags } from 'commons/hooks';
import { ComponentsSidebar, componentsSidebarSections } from 'craft/components/ComponentsTab/types';
import { useComponentsSearch } from 'craft/state';
import { isDomElement } from 'craft/utils/viewport';

const StyledPill = styled(Paragraph)<{ active?: boolean }>`
  ${({ theme, active }) => css`
    display: inline-block;
    padding: ${theme.spaces.xxs} ${theme.spaces.s};
    margin: 0;
    border-radius: 60px;
    border: 1px solid ${theme.colors.Transparent};
    font-size: ${theme.fonts.body.fontSize};
    color: ${theme.colors.OnyxGrayD10};
    cursor: pointer;

    ${active &&
    css`
      border-color: ${theme.colors.BrandPurpleL20};
      background-color: ${theme.colors.BrandPurpleL30};
      color: ${theme.colors.BrandPurple};
      font-weight: ${theme.fontWeights.bold};
    `};

    &:not(:first-child) {
      margin-top: ${theme.spaces.xs};
    }
  `}
`;

export const ComponentsSectionSidebar = () => {
  const search = useComponentsSearch();
  const [availableSections, setAvailableSections] = useState(componentsSidebarSections);
  const [activeIndex, setActiveIndex] = useState(0);
  const [isAutoScrolling, setIsAutoScrolling] = useState(false);

  const autoScrollTimeout = useRef<NodeJS.Timeout>();

  const { [Flags.FORMS]: withForms } = useFlags();
  const { hasEntitlementAccess } = useEntitlementAccess(EntitlementName.Forms);

  useEffect(() => {
    const scrollableContainer = document.getElementById('components-scrollable-container');

    if (!scrollableContainer) {
      return () => undefined;
    }

    const onScroll = () => {
      if (isAutoScrolling) {
        return;
      }

      let targetIndex = activeIndex;
      let stop = false;

      availableSections.forEach((section, index) => {
        if (stop) {
          // if we have found a match, stop iterating
          return;
        }

        const anchoredElements = document.getElementsByClassName(
          section.anchor,
        ) as HTMLCollectionOf<HTMLElement>;
        if (section.anchor && anchoredElements.length && anchoredElements[0].parentElement) {
          const positionFromTop =
            anchoredElements[0].offsetTop -
            anchoredElements[0].parentElement.offsetTop -
            scrollableContainer.scrollTop;

          // this is to accommodate anchored sections that contain more than one subsection (like form container and form fields)
          // it counts on the related anchored sections to be consecutive to work properly
          const sectionHeight = Array.from(anchoredElements).reduce(
            (acc, el) => acc + el.offsetHeight,
            0,
          );

          if (Math.abs(positionFromTop) < sectionHeight) {
            targetIndex = index;
            stop = true;
          }
        }
      });

      setActiveIndex(targetIndex);
    };

    scrollableContainer.addEventListener('scroll', onScroll);

    return () => {
      scrollableContainer.removeEventListener('scroll', onScroll);
    };
  }, [availableSections, activeIndex, isAutoScrolling]);

  useEffect(() => {
    let filteredSections = componentsSidebarSections;
    if (search) {
      filteredSections = componentsSidebarSections.filter(
        ({ anchor }) => !!document.getElementsByClassName(anchor).length,
      );
    }
    setAvailableSections(filteredSections);

    // reset active sidebar section and scroll to top
    setActiveIndex(0);
    const scrollableContainer = document.getElementById('components-scrollable-container');
    if (scrollableContainer) {
      scrollableContainer.scrollTop = 0;
    }
  }, [search]);

  return (
    <>
      {availableSections
        .filter(
          ({ label }) =>
            withForms || hasEntitlementAccess || label !== ComponentsSidebar.Forms.label,
        )
        .map(({ label, anchor }, index) => {
          return (
            <StyledPill
              key={label}
              active={index === activeIndex}
              onClick={() => {
                if (autoScrollTimeout.current) {
                  clearTimeout(autoScrollTimeout.current);
                }

                setActiveIndex(index);

                const targetEl = document.getElementsByClassName(anchor)[0];
                if (isDomElement(targetEl)) {
                  setIsAutoScrolling(true);
                  scrollIntoView(targetEl, { behavior: 'smooth', block: 'start' });
                  autoScrollTimeout.current = setTimeout(() => setIsAutoScrolling(false), 1000);
                }
              }}
              size="short"
            >
              {label}
            </StyledPill>
          );
        })}
    </>
  );
};
